// useAuth Hook - Authentication operations
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '@/stores/auth/authStore';
import { authApi } from '@/services/api/auth.api';
import { ROUTES } from '@/constants/routes';
import type { LoginRequest, RegisterRequest } from '@/types/models/user.types';

export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, token, isAuthenticated, login: setAuthState, logout: clearAuthState } = useAuthStore();

  // Get current user query
  const { refetch: refetchUser } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: authApi.me,
    enabled: !!token && !user,
    retry: false,
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (data) => {
      setAuthState(data.user, data.token);
      notifications.show({
        title: 'Welcome back!',
        message: `Logged in as ${data.user.email}`,
        color: 'blue',
      });
      navigate(ROUTES.DASHBOARD);
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Login failed',
        message: error.message || 'Invalid email or password',
        color: 'red',
      });
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
    onSuccess: (data) => {
      setAuthState(data.user, data.token);
      notifications.show({
        title: 'Welcome to CarLeads!',
        message: 'Your account has been created successfully',
        color: 'green',
      });
      navigate(ROUTES.DASHBOARD);
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Registration failed',
        message: error.message || 'Failed to create account',
        color: 'red',
      });
    },
  });

  // Logout
  const logout = () => {
    clearAuthState();
    queryClient.clear();
    notifications.show({
      title: 'Logged out',
      message: 'You have been logged out successfully',
      color: 'blue',
    });
    navigate(ROUTES.LOGIN);
  };

  return {
    // State
    user,
    token,
    isAuthenticated,

    // Queries
    refetchUser,

    // Mutations
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout,

    // Loading states
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
  };
};
